import { writePersistedState } from "../../../../shared/presentation/hooks/usePersistedState";

/**
 * Where each Home task row sends the user, and what state the destination should
 * open in.
 *
 * The existing list sections keep their filters in persisted state
 * (`usePersistedState`), not in the URL, and they read them in their `useState`
 * initializer. So a deep link is two steps: write the filters, then navigate.
 * `writePersistedState` is a thin additive export on the same shared hook, so
 * both sides share one key-scoping implementation.
 *
 * When these sections migrate into the v1 shell they should accept `?status=`
 * and `?range=` search params, at which point this module collapses to plain
 * paths.
 */

/** One persisted-state write to apply before navigating. */
export interface PersistedWrite {
  key: string;
  value: unknown;
}

export interface TaskTarget {
  /** Router path to navigate to. */
  path: string;
  /** Filters to write before navigating. */
  persist?: PersistedWrite[];
}

const DASHBOARD = "/dashboard";

/**
 * Home counts every outstanding item regardless of age, but the list sections
 * default to a "Last 30 days" window. Without widening the range, clicking
 * "Follow up on 1 bill on hold" would land on a list showing zero — the item is
 * real, just older than 30 days.
 *
 * `usePersistedDateRange` rebuilds the concrete dates from the preset on read,
 * so the preset alone is enough here.
 */
const ALL_TIME = { preset: "all_time" };

const listTarget = (
  path: string,
  statusKey: string,
  status: string,
  dateRangeKey: string
): TaskTarget => ({
  path,
  persist: [
    { key: statusKey, value: status },
    { key: dateRangeKey, value: ALL_TIME },
  ],
});

const INVOICE_STATUS_KEY = "invoices.list.status";
const INVOICE_RANGE_KEY = "invoices.list.dateRange";
const BILL_STATUS_KEY = "bills.list.status";
const BILL_RANGE_KEY = "bills.list.dateRange";
const CASH_BANK_RANGE_KEY = "cashBank.dateRange";

export const TASK_TARGETS = {
  invoicesOnHold: listTarget(
    `${DASHBOARD}/invoices`,
    INVOICE_STATUS_KEY,
    "on_hold",
    INVOICE_RANGE_KEY
  ),
  invoicesUnderReview: listTarget(
    `${DASHBOARD}/invoices`,
    INVOICE_STATUS_KEY,
    "under_review",
    INVOICE_RANGE_KEY
  ),
  invoicesToReview: listTarget(
    `${DASHBOARD}/invoices`,
    INVOICE_STATUS_KEY,
    "pending",
    INVOICE_RANGE_KEY
  ),

  billsOnHold: listTarget(`${DASHBOARD}/bills`, BILL_STATUS_KEY, "on_hold", BILL_RANGE_KEY),
  billsUnderReview: listTarget(
    `${DASHBOARD}/bills`,
    BILL_STATUS_KEY,
    "under_review",
    BILL_RANGE_KEY
  ),
  billsToReview: listTarget(`${DASHBOARD}/bills`, BILL_STATUS_KEY, "pending", BILL_RANGE_KEY),

  // Cash and Bank has no "uncategorised" tab of its own; `pending` is the
  // closest existing filter for work that still needs a decision.
  bankToCategorize: {
    path: `${DASHBOARD}/cash-bank`,
    persist: [
      { key: "cashBank.customer.status", value: "pending" },
      { key: "cashBank.vendor.status", value: "pending" },
      { key: CASH_BANK_RANGE_KEY, value: ALL_TIME },
    ],
  },
  bankOnHold: { path: `${DASHBOARD}/queries` },
  bankFlagged: {
    path: `${DASHBOARD}/cash-bank`,
    persist: [{ key: CASH_BANK_RANGE_KEY, value: ALL_TIME }],
  },

  queriesAssignedToMe: { path: `${DASHBOARD}/queries` },
  queriesPendingClient: { path: `${DASHBOARD}/queries` },
  queriesPendingSenior: { path: `${DASHBOARD}/queries` },

  journalsPendingApproval: { path: `${DASHBOARD}/approvals` },
  journalsRecurring: { path: "/journal/recurring" },
} satisfies Record<string, TaskTarget>;

export type TaskTargetKey = keyof typeof TASK_TARGETS;

/**
 * Applies a target's filters (if any) and returns the path to navigate to.
 */
export const resolveTaskTarget = (target: TaskTarget): string => {
  target.persist?.forEach(({ key, value }) => {
    writePersistedState(key, value);
  });

  return target.path;
};
